import React, { useState } from 'react';

const LookupMappings = ({
  lookupMappings,
  loading,
  fetchLookupMappings,
  handleSaveLookupMapping,
  handleDeleteLookupMapping,
  apiFetch
}) => {
  const [selectedLookupType, setSelectedLookupType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [hubspotOwners, setHubspotOwners] = useState([]);
  const [autotaskResources, setAutotaskResources] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [formData, setFormData] = useState({
    lookup_type: 'Resource',
    hubspot_id: '',
    hubspot_name: '',
    autotask_id: '',
    autotask_name: '',
    is_active: true
  });

  const lookupTypes = ['Resource', 'Company', 'Contact'];

  const filteredMappings = selectedLookupType === 'all'
    ? lookupMappings
    : lookupMappings.filter(m => m.lookup_type === selectedLookupType);

  const fetchDropdownData = async () => {
    setLoadingDropdowns(true);
    try {
      const [ownersResp, resourcesResp] = await Promise.all([
        apiFetch('/hubspot/owners'),
        apiFetch('/autotask/resources')
      ]);
      const ownersRes = await ownersResp.json();
      const resourcesRes = await resourcesResp.json();
      if (ownersRes.success) {
        setHubspotOwners(ownersRes.owners);
      } else {
        console.error('HubSpot owners error:', ownersRes.error);
      }
      if (resourcesRes.success) {
        setAutotaskResources(resourcesRes.resources);
      } else {
        console.error('Autotask resources error:', resourcesRes.error);
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      alert('Error loading dropdown data: ' + error.message);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  const handleCreate = () => {
    setEditingMapping(null);
    setFormData({
      lookup_type: 'Resource',
      hubspot_id: '',
      hubspot_name: '',
      autotask_id: '',
      autotask_name: '',
      is_active: true
    });
    setShowModal(true);
    fetchDropdownData();
  };

  const handleEdit = (mapping) => {
    setEditingMapping(mapping);
    setFormData({
      lookup_type: mapping.lookup_type || 'Resource',
      hubspot_id: mapping.hubspot_id || '',
      hubspot_name: mapping.hubspot_name || '',
      autotask_id: mapping.autotask_id || '',
      autotask_name: mapping.autotask_name || '',
      is_active: mapping.is_active
    });
    setShowModal(true);
    fetchDropdownData();
  };

  const handleHubspotSelect = (e) => {
    const selectedId = e.target.value;
    const owner = hubspotOwners.find(o => o.id === selectedId);
    setFormData({
      ...formData,
      hubspot_id: selectedId,
      hubspot_name: owner ? (owner.name || owner.email) : ''
    });
  };

  const handleAutotaskSelect = (e) => {
    const selectedId = e.target.value;
    const resource = autotaskResources.find(r => r.id === selectedId);
    setFormData({
      ...formData,
      autotask_id: selectedId,
      autotask_name: resource ? (resource.name || resource.email) : ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await handleSaveLookupMapping(formData, editingMapping?.id);
    if (success) setShowModal(false);
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="header-left">
          <h2>Lookup Mappings</h2>
          <p>HubSpot to Autotask ID mappings for owners, resources, etc.</p>
        </div>
        <div className="header-actions">
          <select
            value={selectedLookupType}
            onChange={(e) => setSelectedLookupType(e.target.value)}
            className="entity-select"
          >
            <option value="all">All Types</option>
            {lookupTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <button onClick={fetchLookupMappings} className="btn btn-secondary" disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button onClick={handleCreate} className="btn btn-primary">
            + Add Mapping
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading lookup mappings...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Type</th>
                <th>HubSpot</th>
                <th></th>
                <th>Autotask</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMappings.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">No lookup mappings found</td>
                </tr>
              ) : (
                filteredMappings.map((mapping) => (
                  <tr key={mapping.id} className={!mapping.is_active ? 'inactive' : ''}>
                    <td>
                      <span className={`status-badge ${mapping.is_active ? 'success' : 'error'}`}>
                        {mapping.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <span className="type-badge">{mapping.lookup_type}</span>
                    </td>
                    <td>
                      <code>{mapping.hubspot_id}</code>
                      {mapping.hubspot_name && (
                        <>
                          <br />
                          <small className="text-muted">{mapping.hubspot_name}</small>
                        </>
                      )}
                    </td>
                    <td className="arrow-cell">→</td>
                    <td>
                      <code>{mapping.autotask_id}</code>
                      {mapping.autotask_name && (
                        <>
                          <br />
                          <small className="text-muted">{mapping.autotask_name}</small>
                        </>
                      )}
                    </td>
                    <td>
                      <button onClick={() => handleEdit(mapping)} className="btn btn-sm btn-edit">
                        ✏️
                      </button>
                      <button onClick={() => handleDeleteLookupMapping(mapping.id)} className="btn btn-sm btn-delete">
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div style={{
        marginTop: 24,
        padding: 16,
        background: '#f8f9fa',
        borderRadius: 8,
        border: '1px solid #e9ecef'
      }}>
        <h4 style={{margin: '0 0 8px 0', fontSize: 14, color: '#495057'}}>How Lookup Mappings Work</h4>
        <p style={{margin: 0, fontSize: 13, color: '#6c757d', lineHeight: 1.6}}>
          Lookup mappings connect HubSpot IDs to Autotask IDs for related entities like owners and resources.
          For example, a HubSpot owner ID "85746163" maps to Autotask resource "29682887".
          When a record is synced, these lookups ensure the correct owner/resource is assigned in Autotask.
          The default mapping is used when no specific match is found.
        </p>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingMapping ? 'Edit Lookup Mapping' : 'Create Lookup Mapping'}</h3>
              <button onClick={() => setShowModal(false)} className="modal-close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Lookup Type</label>
                  <select
                    value={formData.lookup_type}
                    onChange={(e) => setFormData({ ...formData, lookup_type: e.target.value })}
                  >
                    {lookupTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>HubSpot Owner</label>
                  {loadingDropdowns ? (
                    <select disabled><option>Loading...</option></select>
                  ) : (
                    <select
                      value={formData.hubspot_id}
                      onChange={handleHubspotSelect}
                      required
                    >
                      <option value="">-- Select HubSpot Owner --</option>
                      {hubspotOwners.map(owner => (
                        <option key={owner.id} value={owner.id}>
                          {owner.name} ({owner.email}) - ID: {owner.id}
                        </option>
                      ))}
                    </select>
                  )}
                  {formData.hubspot_id && (
                    <small className="text-muted">
                      ID: {formData.hubspot_id} | Name: {formData.hubspot_name}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Autotask Resource</label>
                  {loadingDropdowns ? (
                    <select disabled><option>Loading...</option></select>
                  ) : (
                    <select
                      value={formData.autotask_id}
                      onChange={handleAutotaskSelect}
                      required
                    >
                      <option value="">-- Select Autotask Resource --</option>
                      {autotaskResources.map(resource => (
                        <option key={resource.id} value={resource.id}>
                          {resource.name} ({resource.email}) - ID: {resource.id}
                        </option>
                      ))}
                    </select>
                  )}
                  {formData.autotask_id && (
                    <small className="text-muted">
                      ID: {formData.autotask_id} | Name: {formData.autotask_name}
                    </small>
                  )}
                </div>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingMapping ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LookupMappings;
